/**
 * Insight Rotation Service
 * 
 * Fetches additional insights when the current daily insight plan is exhausted.
 * Uses a hybrid approach:
 * 1. First tries to get more insights from already-recommended books
 * 2. Falls back to vector search for new relevant insights
 */

import { BookRepository } from '../repositories/bookRepository';
import { BookService } from './bookService';
import { PrivateProfileService } from './privateProfileService';
import { AIInsightRecommendation } from '../models/PrivateProfile';

export class InsightRotationService {
  /**
   * Fetch additional insights to add to the rotation
   * 
   * Strategy:
   * 1. Get insights from user's recommended books (already personalized)
   * 2. Filter out insights already in the plan
   * 3. If not enough, use vector search for more
   * 
   * @param userId - User ID
   * @param existingInsightIds - Insight IDs already in the plan
   * @param languageCode - Language code (default: 'en')
   * @param targetCount - Number of new insights to fetch (default: 5)
   * @returns Array of new insight recommendations
   */
  static async fetchAdditionalInsights(
    userId: string,
    existingInsightIds: string[],
    languageCode: string = 'en',
    targetCount: number = 10
  ): Promise<AIInsightRecommendation[]> {
    try {
      console.log('🔄 Fetching additional insights for rotation...');
      const finalTargetCount = Math.min(targetCount, 10); // cap at top 10 as requested
      
      // Step 1: Get user's profile and current recommendations
      const profileResult = await PrivateProfileService.getProfile(userId);
      if (!profileResult.success || !profileResult.data) {
        console.warn('⚠️ No profile found, cannot fetch additional insights');
        return [];
      }

      const profile = profileResult.data;
      const recommendations = profile.currentRecommendations;
      
      if (!recommendations?.recommendedBooks || recommendations.recommendedBooks.length === 0) {
        console.warn('⚠️ No recommended books found');
        return await this.fallbackToVectorSearch(userId, profile, languageCode, targetCount);
      }

      // Step 2: Get all insights from recommended books
      const bookIds = recommendations.recommendedBooks
        .map(book => book.bookId)
        .filter(id => !!id);
      
      console.log(`📚 Fetching insights from ${bookIds.length} recommended books...`);
      
      const allInsights = await BookRepository.getInsightsByBookIds(
        bookIds,
        languageCode,
        finalTargetCount * 2 // grab some buffer but still bounded
      );

      // Step 3: Filter out insights already in the plan or shown, dedupe
      const seen = new Set<string>(existingInsightIds);
      const bookInsights: typeof allInsights = [];
      allInsights.forEach(insight => {
        if (!seen.has(insight.id)) {
          seen.add(insight.id);
          bookInsights.push(insight);
        }
      });

      console.log(`✅ Found ${bookInsights.length} new insights from recommended books`);

      const bookRecommendations = bookInsights.slice(0, finalTargetCount).map(insight => ({
        insightId: insight.id,
        reason: `From your recommended reading: ${insight.bookTitle || 'recommended book'}`,
        bookId: insight.bookId,
        recommendedAt: Date.now() / 1000,
      }));

      // Step 4: If we have enough, return them
      if (bookRecommendations.length >= finalTargetCount) {
        return bookRecommendations;
      }

      // Step 5: Not enough insights, add from vector search
      console.log(`⚠️ Only ${bookInsights.length} insights available, using vector search for more...`);
      
      const vectorInsights = await this.fallbackToVectorSearch(
        userId,
        profile,
        languageCode,
        finalTargetCount - bookInsights.length
      );

      // Deduplicate vector results against seen set
      const filteredVector = vectorInsights.filter(insight => {
        if (seen.has(insight.insightId)) {
          return false;
        }
        seen.add(insight.insightId);
        return true;
      });

      // Combine insights from books and vector search and cap to top N
      const combined = [...bookRecommendations, ...filteredVector].slice(0, finalTargetCount);

      return combined;

    } catch (error: any) {
      console.error('❌ Error fetching additional insights:', error);
      return [];
    }
  }

  /**
   * Fallback: Use vector search to find relevant insights
   */
  private static async fallbackToVectorSearch(
    userId: string,
    profile: any,
    languageCode: string,
    count: number
  ): Promise<AIInsightRecommendation[]> {
    try {
      // Build search query from user's core profile
      const searchTerms: string[] = [];
      
      if (profile.coreProfile?.mainFocusLabel) {
        searchTerms.push(profile.coreProfile.mainFocusLabel);
      }
      
      if (profile.coreProfile?.goals && profile.coreProfile.goals.length > 0) {
        searchTerms.push(...profile.coreProfile.goals.slice(0, 2)); // Top 2 goals
      }
      
      if (profile.coreProfile?.interests && profile.coreProfile.interests.length > 0) {
        searchTerms.push(...profile.coreProfile.interests.slice(0, 2)); // Top 2 interests
      }

      const searchQuery = searchTerms.join(', ') || 'personal growth';
      
      console.log(`🔍 Vector search for insights: "${searchQuery}"`);

      const vectorResult = await BookService.vectorSearch(
        searchQuery,
        ['insight'], // Only search for insights
        languageCode,
        count * 2, // Get more than needed for filtering
        count
      );

      if (!vectorResult.success || !vectorResult.data?.insights) {
        console.warn('⚠️ Vector search failed');
        return [];
      }

      // Filter out insights we might already have
      const existingInsightIds = profile.currentRecommendations?.insightPlan?.insights?.map(
        (i: any) => i.insightId
      ) || [];

      const newInsights = vectorResult.data.insights
        .filter(insight => !existingInsightIds.includes(insight.id))
        .slice(0, count);

      console.log(`✅ Vector search found ${newInsights.length} new insights`);

      return newInsights.map(insight => ({
        insightId: insight.id,
        reason: `Recommended based on your interests`,
        bookId: insight.bookId,
        recommendedAt: Date.now() / 1000,
      }));

    } catch (error: any) {
      console.error('❌ Vector search fallback failed:', error);
      return [];
    }
  }
}

